import { IdentifierTypeEnum } from "@repo/graphql/gql/graphql";
import { formatCuit } from "../cuit";
import { formatNit } from "../nit";
import { formatRfc } from "../rfc";
import { formatRuc } from "../ruc";
import { formatRut } from "../rut";

// Display side of a country's tax document: its short name and how people
// write it. Collecting the value (types, hints, validation) is the
// identifier input's job, `@repo/components/src/forms/identifier`.
export type IdentifierStrategy = {
  label: string;
  format: (value: string) => string;
};

const rutStrategy: IdentifierStrategy = {
  label: "RUT",
  format: formatRut,
};

const rucStrategy: IdentifierStrategy = {
  label: "RUC",
  format: formatRuc,
};

const rfcStrategy: IdentifierStrategy = {
  label: "RFC",
  format: formatRfc,
};

const nitStrategy: IdentifierStrategy = {
  label: "NIT",
  format: formatNit,
};

const cuitStrategy: IdentifierStrategy = {
  label: "CUIT",
  format: formatCuit,
};

const defaultStrategy: IdentifierStrategy = {
  label: "Identificador",
  format: (value: string) => value,
};

const IDENTIFIER_STRATEGIES: Record<string, IdentifierStrategy> = {
  CL: rutStrategy,
  PE: rucStrategy,
  MX: rfcStrategy,
  CO: nitStrategy,
  AR: cuitStrategy,
};

export const getIdentifierStrategy = (
  countryCode: string | null | undefined,
): IdentifierStrategy => {
  if (!countryCode) return defaultStrategy;
  return IDENTIFIER_STRATEGIES[countryCode] ?? defaultStrategy;
};

const IDENTIFIER_STRATEGIES_BY_TYPE: Partial<
  Record<IdentifierTypeEnum, IdentifierStrategy>
> = {
  [IdentifierTypeEnum.ClRut]: rutStrategy,
  [IdentifierTypeEnum.PeRuc]: rucStrategy,
  [IdentifierTypeEnum.MxRfc]: rfcStrategy,
  [IdentifierTypeEnum.CoNit]: nitStrategy,
  [IdentifierTypeEnum.ArCuit]: cuitStrategy,
};

/**
 * The display strategy of a tax document. Personal documents (DNI,
 * passport...) have none: they are shown as typed.
 */
export const getIdentifierStrategyByType = (
  type: IdentifierTypeEnum | null | undefined,
): IdentifierStrategy | undefined =>
  type ? IDENTIFIER_STRATEGIES_BY_TYPE[type] : undefined;
